import _Button from '@design-system/components/atoms/Button/_Button'
import { css } from '@emotion/react'
import { colors, typo } from '@mathflat/common/style'
import { observer } from 'mobx-react'
import { useEffect } from 'react'

import Checkbox from '@/component/atoms/Checkbox/_Checkbox/_Checkbox'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import PlusMinusInput from '@/component/molecules/PlusMinusInput'
import useRepository from '@/hooks/useRepository'
import { WorksheetBySchoolTestService } from '@/page/Lesson-prepare/PageSchoolTest/services/WorksheetBySchoolTest.service'
import { CommonPrintService, worksheetPrintService } from '@/service/print'

interface Props {
  onRequestPrint: () => void
}

export const SchoolTestPrintModal = observer(({ onRequestPrint }: Props) => {
  const service = useRepository(WorksheetBySchoolTestService)

  useEffect(() => {
    initialSync()
  }, [])

  const initialSync = async () => {
    await worksheetPrintService.fetchInitPrintList(service.checkedSchoolTestIds, [])

    worksheetPrintService.uiStore.update({
      visibleStudentOption: 'NONE',
    })
    worksheetPrintService.rangeOption.update({
      answer: false,
      omr: false,
    })
  }

  const isPrintEnabled = (() => {
    if (worksheetPrintService.uiStore.visibleRangeOption === 'SELECT') {
      return (
        worksheetPrintService.rangeOption.problem || worksheetPrintService.rangeOption.quickAnswer
      )
    }
    return true
  })()

  return (
    <section css={_css}>
      <header>
        <h3>기출 시험지 인쇄</h3>
      </header>
      <div className="print-body">
        <div className="print-row print-count">
          <p className="row-left">매수</p>
          <div className="row-right">
            <PlusMinusInput
              value={worksheetPrintService.printOption.copyNumber}
              max={CommonPrintService.MAX_PRINT_QUANTITY}
              min={1}
              onChange={(copyNumber) => {
                worksheetPrintService.printOption.update({ copyNumber })
              }}
              editable
            />
          </div>
        </div>

        {worksheetPrintService.printAgent && (
          <div className="print-row printer-select">
            <p className="row-left">프린터 선택</p>
            <div className="row-right">
              <div className="print-dropdown">
                <Dropdown
                  height={40}
                  width={344}
                  listHeight={120}
                  value={`${worksheetPrintService.printAgent.selectedPrinter?.machineId}:${worksheetPrintService.printAgent?.selectedPrinter?.displayName}`}
                  onChange={(value: string) => {
                    const [mid, printName] = value.split(':')
                    const printer = worksheetPrintService.printAgent?.printerList.find(
                      ({ displayName, machineId }) =>
                        displayName === printName && machineId === mid,
                    )

                    worksheetPrintService.selectPrinter(printer!)
                  }}
                >
                  {worksheetPrintService.printAgent.printerList.map((printer) => (
                    <DropdownItem
                      key={`${printer.machineId}:${printer.displayName}`}
                      data-mathflat-unique-id={`${printer.machineId}:${printer.displayName}`}
                      value={`${printer.machineId}:${printer.displayName}`}
                    >
                      {printer.displayName}
                    </DropdownItem>
                  ))}
                </Dropdown>
              </div>
            </div>
          </div>
        )}
        <div className="print-row print-option">
          <p className="row-left">옵션</p>
          <div className="row-right row__print-option">
            <Checkbox
              onChange={(isDuplex) => worksheetPrintService.printOption.update({ isDuplex })}
              checked={worksheetPrintService.printOption.isDuplex}
              text={<span>양면</span>}
            />
            <Checkbox
              onChange={(isMonochrome) =>
                worksheetPrintService.printOption.update({ isMonochrome })
              }
              checked={worksheetPrintService.printOption.isMonochrome}
              text={<span>흑백</span>}
            />
            <Checkbox
              onChange={(isReverse) => worksheetPrintService.printOption.update({ isReverse })}
              checked={worksheetPrintService.printOption.isReverse}
              text={<span>역순</span>}
            />
          </div>
        </div>
      </div>
      <div className="print-footer">
        <_Button
          minWidth={144}
          size="m"
          disabled={!isPrintEnabled}
          theme="primary"
          onClick={onRequestPrint}
        >
          인쇄하기
        </_Button>
      </div>
    </section>
  )
})

const _css = css`
  width: 546px;

  header {
    display: flex;
    justify-content: space-between;
    padding: 24px;
    height: 72px;
    border-bottom: 1px solid ${colors.gray.$400};

    h3 {
      ${typo.heading05}
      font-weight: 700;
    }
  }

  .print-body {
    padding: 24px 24px 0;
  }

  .print-row {
    display: flex;
    align-items: center;
    gap: 24px;
    padding-bottom: 32px;
  }

  .row-left {
    width: 84px;
    ${typo.body02}
    color: ${colors.gray.$700};
  }

  .row-right {
    display: flex;
    align-items: center;
  }

  .print-footer {
    padding: 0 24px 24px;
    display: flex;
    justify-content: flex-end;
  }

  .row__print-option {
    display: flex;
    gap: 24px;
  }
`
